import { action, makeObservable, observable, runInAction } from "mobx";
import VendorAPI from "../api/VendorAPI";
import {
  GetVendorRequestsParams,
  GetVendorsParams,
  Vendor,
} from "../models/Vendors/Vendor";
import { VendorPOC } from "../models/Vendors/VendorPOC";

export class VendorStore {
  VendorAPI: VendorAPI;
  vendors: Vendor[];
  vendorRequests: Vendor[];
  limit: number;
  total_pages: number;
  total_count: number;
  vendorDetails: Vendor | undefined;
  financeApprovePopup: { open: boolean; vendorId: number | null };
  financeRejectPopup: { open: boolean; vendorId: number | null };
  sourcingApprovePopup: { open: boolean; vendorId: number | null };
  sourcingRejectPopup: { open: boolean; vendorId: number | null };
  viewDetailsPopup: { open: boolean; vendorId: number | null };
  selectedFilters: {
    missingDocuments: string[];
    status: string;
    vendorType: string[];
    vendorSupplierTypes: string;
  };

  constructor() {
    this.VendorAPI = new VendorAPI();
    this.vendors = [];
    this.vendorRequests = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.vendorDetails = {};
    this.financeApprovePopup = { open: false, vendorId: null };
    this.financeRejectPopup = { open: false, vendorId: null };
    this.sourcingApprovePopup = { open: false, vendorId: null };
    this.sourcingRejectPopup = { open: false, vendorId: null };
    this.viewDetailsPopup = { open: false, vendorId: null };
    this.selectedFilters = {
      missingDocuments: [],
      status: "0",
      vendorType: [],
      vendorSupplierTypes: "0",
    };

    makeObservable(this, {
      vendors: observable,
      vendorRequests: observable,
      vendorDetails: observable,
      financeApprovePopup: observable,
      financeRejectPopup: observable,
      sourcingApprovePopup: observable,
      sourcingRejectPopup: observable,
      viewDetailsPopup: observable,
      selectedFilters: observable,
      getVendors: action,
      addPoc: action,
      removeLocalPoc: action,
      updateVendorDetails: action,
      updateVendorType: action,
      getVendorRequest: action,
      openFinanceApprovePopup: action,
      closeFinanceApprovePopup: action,
      openFinanceRejectPopup: action,
      closeFinanceRejectPopup: action,
      openSourcingApprovePopup: action,
      closeSourcingApprovePopup: action,
      openSourcingRejectPopup: action,
      closeSourcingRejectPopup: action,
      approveFinanceVendorRequest: action,
      rejectFinanceVendorRequest: action,
      approveSourcingVendorRequest: action,
      rejectSourcingVendorRequest: action,
      openViewDetailsPopup: action,
      closeViewDetailsPopup: action,
      setSelectedFilters: action,
      resetSelectedFilters: action,
      initializeSelectedFiltersFromUrl: action,
    });
  }

  getVendors = async (params: GetVendorsParams) => {
    try {
      const response = await this.VendorAPI.getVendors(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.vendors = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getVendorById = async (vendorId: number) => {
    try {
      const response = await this.VendorAPI.getVendorDetailsById(vendorId);
      this.vendorDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  updateVendorById = async (vendor: Vendor) => {
    try {
      const response = await this.VendorAPI.updateVendorDetailsById(vendor);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteVendor = async (vendorId: number) => {
    try {
      const response = await this.VendorAPI.deleteVendorById(vendorId);

      if (this.vendors.length > 0) {
        const index = this.vendors.findIndex(
          (vendor) => vendor.id === vendorId
        );
        if (index !== -1) {
          this.vendors = this.vendors.filter(
            (vendor) => vendor.id !== vendorId
          );
        }
      }

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteVendorPoc = async (vendorId: number, pocId: number) => {
    try {
      const response = await this.VendorAPI.deleteVendorPocById(
        vendorId,
        pocId
      );

      if (
        this.vendorDetails?.poc_list &&
        this.vendorDetails.poc_list.length > 0
      ) {
        const index = this.vendorDetails.poc_list.findIndex(
          (poc) => poc.id === pocId
        );
        if (index !== -1) {
          this.vendorDetails.poc_list = this.vendorDetails.poc_list.filter(
            (poc) => poc.id !== pocId
          );
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  addVendor = async () => {
    try {
      const response = await this.VendorAPI.addNewVendor(this.vendorDetails!);
      if (response && this.vendorDetails) {
        this.vendorDetails.id = response as number;
        this.vendors.push(this.vendorDetails);
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateVendorDetails = (name: string, value: string) => {
    this.vendorDetails = {
      ...this.vendorDetails,
      [name]: value,
    };
  };

  addPoc = (poc: VendorPOC) => {
    if (this.vendorDetails?.poc_list) {
      const hasPrimary = this.vendorDetails.poc_list.some((p) => {
        return (
          p.is_primary === "1" ||
          String(p.is_primary) === "1"
        );
      });
      const { is_primary, ...restPoc } = poc;
      const newPoc = hasPrimary
        ? { ...restPoc, is_primary: "0" }
        : { ...restPoc, is_primary: "1" };
      this.vendorDetails.poc_list = [...this.vendorDetails?.poc_list, newPoc];
    } else {
      const { is_primary, ...restPoc } = poc;
      this.vendorDetails!.poc_list = [{ ...restPoc, is_primary: "1" }];
    }
  };

  removeLocalPoc = (index: number) => {
    runInAction(() => {
      if (
        this.vendorDetails?.poc_list &&
        this.vendorDetails.poc_list.length > 0 &&
        index >= 0 &&
        index < this.vendorDetails.poc_list.length
      ) {
        const removedPoc = this.vendorDetails.poc_list[index];
        const wasPrimary =
          removedPoc.is_primary === "1" ||
          String(removedPoc.is_primary) === "1";

        this.vendorDetails.poc_list = this.vendorDetails.poc_list.filter(
          (_, i) => i !== index
        );

        if (wasPrimary && this.vendorDetails.poc_list.length > 0) {
          this.vendorDetails.poc_list[0].is_primary = "1";
          for (let i = 1; i < this.vendorDetails.poc_list.length; i++) {
            this.vendorDetails.poc_list[i].is_primary = "0";
          }
        }
      }
    });
  };

  updateVendorAddress = (name: string, value: string) => {
    if (this.vendorDetails?.address) {
      this.vendorDetails.address = {
        ...this.vendorDetails.address,
        [name]: value,
      };
    } else {
      this.vendorDetails!.address = {
        ...this.vendorDetails?.address,
        [name]: value,
      };
    }
  };

  updateVendorType = (id: number) => {
    if (this.vendorDetails?.types && this.vendorDetails.types.length > 0) {
      const index = this.vendorDetails.types.findIndex(
        (vendorType) => vendorType.type_id === id
      );
      if (index !== -1) {
        this.vendorDetails.types = this.vendorDetails.types.filter(
          (type) => type.type_id !== id
        );
      } else {
        this.vendorDetails.types = [
          ...this.vendorDetails.types,
          { type_id: id },
        ];
      }
    } else {
      this.vendorDetails!.types = [{ type_id: id }];
    }
  };

  getVendorRequest = async (params: GetVendorRequestsParams) => {
    try {
      const response = await this.VendorAPI.getVendorRequest(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.vendorRequests = response.list;
    } catch (error: any) {
      throw error;
    }
  };



  openFinanceApprovePopup = (vendorId: number) => {
    runInAction(() => {
      this.financeApprovePopup = { open: true, vendorId };
    });
  };

  closeFinanceApprovePopup = () => {
    runInAction(() => {
      this.financeApprovePopup = { open: false, vendorId: null };
    });
  };

  openFinanceRejectPopup = (vendorId: number) => {
    runInAction(() => {
      this.financeRejectPopup = { open: true, vendorId };
    });
  };

  closeFinanceRejectPopup = () => {
    runInAction(() => {
      this.financeRejectPopup = { open: false, vendorId: null };
    });
  };

  openSourcingApprovePopup = (vendorId: number) => {
    runInAction(() => {
      this.sourcingApprovePopup = { open: true, vendorId };
    });
  };

  closeSourcingApprovePopup = () => {
    runInAction(() => {
      this.sourcingApprovePopup = { open: false, vendorId: null };
    });
  };

  openSourcingRejectPopup = (vendorId: number) => {
    runInAction(() => {
      this.sourcingRejectPopup = { open: true, vendorId };
    });
  };

  closeSourcingRejectPopup = () => {
    runInAction(() => {
      this.sourcingRejectPopup = { open: false, vendorId: null };
    });
  };

  approveFinanceVendorRequest = async (vendorId: number, approvedByAdminid?: number, approvedByAdminname?: string) => {
    try {
      const response = await this.VendorAPI.approveFinanceVendorRequest(vendorId);
      runInAction(() => {
        this.vendorRequests = this.vendorRequests.map((vendor) =>
          vendor.id === vendorId
            ? {
              ...vendor,
              finance_approval: 1,
              finance_approval_date: new Date().toISOString(),
              finance_approved_by_admin_id: approvedByAdminid || undefined,
              finance_approved_by_admin_name: approvedByAdminname || undefined,
            }
            : vendor
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  rejectFinanceVendorRequest = async (vendorId: number, rejectionReason?: string, rejectedByAdminid?: number, rejectedByAdminname?: string) => {
    try {
      const response = await this.VendorAPI.rejectFinanceVendorRequest(vendorId, rejectionReason);
      runInAction(() => {
        this.vendorRequests = this.vendorRequests.map((vendor) =>
          vendor.id === vendorId
            ? {
              ...vendor,
              finance_rejection: 1,
              finance_rejection_date: new Date().toISOString(),
              finance_rejection_reason: rejectionReason || undefined,
              finance_rejection_by_admin_id: rejectedByAdminid || undefined,
              finance_rejected_by_admin_name: rejectedByAdminname || undefined,
            }
            : vendor
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  approveSourcingVendorRequest = async (vendorId: number, approvedByAdminid?: number, approvedByAdminname?: string) => {
    try {
      const response = await this.VendorAPI.approveSourcingVendorRequest(vendorId);
      runInAction(() => {
        this.vendorRequests = this.vendorRequests.map((vendor) =>
          vendor.id === vendorId
            ? {
              ...vendor,
              sourcing_approval: 1,
              sourcing_approval_date: new Date().toISOString(),
              sourcing_approval_by_admin_id: approvedByAdminid || undefined,
              sourcing_approved_by_admin_name: approvedByAdminname || undefined,
            }
            : vendor
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  rejectSourcingVendorRequest = async (vendorId: number, rejectionReason?: string, rejectedByAdminid?: number, rejectedByAdminname?: string) => {
    try {
      const response = await this.VendorAPI.rejectSourcingVendorRequest(vendorId, rejectionReason);
      runInAction(() => {
        this.vendorRequests = this.vendorRequests.map((vendor) =>
          vendor.id === vendorId
            ? {
              ...vendor,
              sourcing_rejection: 1,
              sourcing_rejection_date: new Date().toISOString(),
              sourcing_rejection_reason: rejectionReason,
              sourcing_rejection_by_admin_id: rejectedByAdminid || undefined,
              sourcing_rejected_by_admin_name: rejectedByAdminname || undefined,
            }
            : vendor
        );
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  openViewDetailsPopup = (vendorId: number) => {
    runInAction(() => {
      this.viewDetailsPopup = { open: true, vendorId };
    });
  };

  closeViewDetailsPopup = () => {
    runInAction(() => {
      this.viewDetailsPopup = { open: false, vendorId: null };
    });
  };

  setSelectedFilters = (filters: {
    missingDocuments: string[];
    status: string;
    vendorType: string[];
    vendorSupplierTypes?: string;
  }) => {
    runInAction(() => {
      this.selectedFilters = {
        ...this.selectedFilters,
        ...filters,
        vendorSupplierTypes: filters.vendorSupplierTypes ?? this.selectedFilters.vendorSupplierTypes,
      };
    });
  };

  resetSelectedFilters = () => {
    runInAction(() => {
      this.selectedFilters = {
        missingDocuments: [],
        status: "0",
        vendorType: [],
        vendorSupplierTypes: "0",
      };
    });
  };

  initializeSelectedFiltersFromUrl = (searchParams: URLSearchParams) => {
    const missingDocs: string[] = [];
    if (searchParams.get("missing_gst_doc") === "true") missingDocs.push("gst_certificate");
    if (searchParams.get("missing_pan_doc") === "true") missingDocs.push("pan_card");
    if (searchParams.get("missing_bank_details") === "true") missingDocs.push("bank_details");
    if (searchParams.get("missing_product_catalog") === "true") missingDocs.push("product_catalog");
    if (searchParams.get("missing_msme_doc") === "true") missingDocs.push("msme_certificate");

    const statusParam = searchParams.get("status");
    let statusValue = "0";
    if (statusParam === "1") statusValue = "1";
    else if (statusParam === "2") statusValue = "2";
    else if (statusParam === "3") statusValue = "3";

    const vendorTypeParam = searchParams.get("vendor_types");
    const vendorTypes: string[] = [];
    if (vendorTypeParam && vendorTypeParam.trim()) {
      const types = vendorTypeParam.split(",").map(t => t.trim()).filter(t => t && t !== "0");
      vendorTypes.push(...types);
    }

    const vendorSupplierTypeParam = searchParams.get("vendor_supplier_types");
    let vendorSupplierTypeValue = "0";
    if (vendorSupplierTypeParam && vendorSupplierTypeParam.trim()) {
      const type = vendorSupplierTypeParam.trim();
      if (type !== "0" && (type === "1" || type === "2")) {
        vendorSupplierTypeValue = type;
      }
    }

    runInAction(() => {
      this.selectedFilters = {
        missingDocuments: missingDocs,
        status: statusValue,
        vendorType: vendorTypes,
        vendorSupplierTypes: vendorSupplierTypeValue,
      };
    });
  };
}
